import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Col, Container, Row } from "reactstrap";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import type { DatePickerProps } from "antd";
import { DatePicker } from "antd";
import {
  CompactSelect,
  CompactTextField,
} from "../../../Components/SharedComponents/SharedComponents";
import axiosInstance from "../../../Utils/axiosInstance";
import { backendbaseUrl } from "../../../Utils/baseUrl";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUpload from "../../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
// Define TypeScript interface for the API data
interface FuturesLTD {
  contract_month: string;
  ls_gas_oil_futures: string;
  brent_crude_futures: string;
  rbob_gasoline_futures: string;
  heating_oil_futures: string;
  wti_crude_futures: string;
}
const Example = () => {
  const [secondTableData, setSecondTableData] = useState<FuturesLTD[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file); // The key should match your API expectation

    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/futures-ltd-upload/`, // Updated endpoint
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Refresh table data after successful upload
      queryClient.invalidateQueries({ queryKey: ["users"] });

      return {
        success: true,
        message: "File uploaded and data added successfully!",
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload file",
      };
    }
  };
  // Fetch data function adapted from provided code
  const fetchData = async () => {
    try {
      
      let url = `${backendbaseUrl}/paper-app/futures-ltd/`;

      const response = await axiosInstance.get(url);
      console.log("API Response:", response.data);

      // let apiData = response.data;
      let apiData: FuturesLTD[] = response.data;
      // Handle different response formats

      console.log("Processed API Data:", apiData);
      // if (!Array.isArray(apiData)) {
      //   if (
      //     typeof apiData === "object" &&
      //     apiData !== null &&
      //     ("contract_month" in apiData ||
      //       "ls_gas_oil_futures" in apiData ||
      //       "brent_crude_futures" in apiData)
      //   ) {
      //     apiData = [apiData] as FuturesLTD[];
      //   } else {
      //     console.warn("Unknown API response format:", apiData);
      //     apiData = [];
      //   }
      // }
      // Set secondTableData
      setSecondTableData(apiData);
    } catch (err) {
      console.error("Error fetching data:", err);
      setSecondTableData([]);
      setError("Failed to fetch data. Please try again.");
    } 
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData(); // Call without parameters initially
  }, []);

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log(rowData);
  };

  // Utility to create a default column configuration for the second table
  const createColumnConfig = (
    accessorKey: string,
    header: string,
    overrides: Partial<MRT_ColumnDef<any>> = {}
  ): MRT_ColumnDef<any> => ({
    accessorKey,
    header,
    enableEditing: false,
    filterVariant: "text",
    muiTableBodyCellProps: ({ row, table }) => ({
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(44, 4, 4, 0.05)",
        },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    }),
    ...overrides,
  });

  // Columns for the second table
  const secondTableColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      createColumnConfig("contract_month", "Contract Months"),
      createColumnConfig("ls_gas_oil_futures", "Ls Gas Oil Futures"),
      createColumnConfig("brent_crude_futures", "Brent Crude Futures"),
      createColumnConfig("rbob_gasoline_futures", "RBOB Gasoline Futures"),
      createColumnConfig("heating_oil_futures", "Heating Oil Futures"),
      createColumnConfig("wti_crude_futures", "WTI Crude Futures"),
    ],
    []
  );

  // Table instance for the second table
  const Table = useMaterialReactTable({
    enableColumnFilterModes: true,
    initialState: { showColumnFilters: true },
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableStickyHeader: true,
    enableGrouping: true,
    enableHiding: true,
    manualSorting: true,
    manualPagination: true,
    columns: secondTableColumns,
    data: secondTableData,
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: false,
    enableColumnOrdering: true,
    enableRowOrdering: false,
    enableRowNumbers: true,
    getRowId: (row) => row.id || Math.random().toString(),
    enableStickyFooter: true,
    enableColumnPinning: true,
    positionToolbarAlertBanner: "bottom",
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    defaultColumn: { minSize: 50, enableResizing: true },
    enableFilterMatchHighlighting: true,
    enableGlobalFilter: false,
    manualFiltering: true,
    enableSorting: true,
    filterFns: {
      customFilterFn: (row, id, filterValue) => {
        return row.getValue(id) === filterValue;
      },
    },
    defaultDisplayColumn: {
      enableResizing: true,
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "italic",
        fontWeight: "normal",
      },
    },
    muiTableBodyCellProps: { sx: { border: "1px solid #ebe9e98f" } },
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: "0", border: "0" },
    },
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        className="table-download__wrapper"
        sx={{
          display: "flex",
          gap: "6px",
          padding: "0px",
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
          Import File
        </Button>
        <Button startIcon={<BrowserUpdatedIcon />}>Export All Data</Button>
        <BrokerTemplateDownloadButton
          apiEndpoint="/paper-app/download-ltd-template/"
          buttonText="Download Template"
          fileName="futures_ltd_template.csv"
        />
        <Button
          disabled={
            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
          }
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
      </Box>
    ),
  });




  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Futures LTD
                  </Accordion.Header>
                  <Accordion.Body></Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="table-ad__wrapper tablePaperPosition">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={Table} />
                </div>
                <FileUpload
                                  open={openFileUpload}
                                  onFileUploadClose={handleFileUploadClose}
                                  onFileUploadSubmit={handleFileUploadSubmit}
                                  acceptedFileTypes=".csv,.json"
                                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

// App component with React Query setup
const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      {/* <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction />
      </Suspense> */}
    </QueryClientProvider>
  );
}
